import { Button, Input, Space, Typography } from 'antd';
import React, { useState } from 'react';

import type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import { useSharePointLinkResolver } from '@/hooks/useSharePointLinkResolver';

const { Paragraph } = Typography;

export interface SharePointLinkResolverProps {
  accessToken?: string | null;
  microsoftOboAssertion?: string | null;
  clientId: string;
  projectId: string;
  resolved: ResolveSharePointSharingLinkResponseDTO | null;
  onResolved: (resolved: ResolveSharePointSharingLinkResponseDTO | null) => void;
}

/**
 * Paste-a-sharing-link-and-resolve widget for binding a SharePoint/OneDrive folder.
 * Purely presentational — the caller decides what to do with the resolved DTO
 * (e.g. persist it onto a project record, or use its web URL as a project link).
 */
export function SharePointLinkResolver({
  accessToken,
  microsoftOboAssertion,
  clientId,
  projectId,
  resolved,
  onResolved,
}: Readonly<SharePointLinkResolverProps>): React.ReactElement {
  const [draft, setDraft] = useState('');
  const { resolving, resolveLink } = useSharePointLinkResolver(
    accessToken,
    clientId,
    projectId,
    microsoftOboAssertion,
  );

  const handleClear = (): void => {
    setDraft('');
    onResolved(null);
  };

  return (
    <div className="sharepoint-link-resolver">
      <label htmlFor="sharepoint-sharing-link">Sharing link</label>
      <Input.TextArea
        id="sharepoint-sharing-link"
        rows={2}
        placeholder="https://…sharepoint.com/:f:/s/…"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <Space style={{ marginTop: 8, marginBottom: 8 }}>
        <Button onClick={() => void resolveLink(draft, onResolved)} loading={resolving}>
          Resolve link
        </Button>
        <Button onClick={handleClear}>Clear</Button>
      </Space>
      {resolved?.sharepoint_folder_web_url ? (
        <Paragraph>
          Resolved folder:{' '}
          <a href={resolved.sharepoint_folder_web_url} target="_blank" rel="noopener noreferrer">
            Open in SharePoint
          </a>
        </Paragraph>
      ) : null}
    </div>
  );
}
